/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  // TableProps,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { IDataModal } from '@/interfaces/dataModal';
import { IProduct, IUpdateProductAllocation } from '@/interfaces/product';
import { addProduct, editProduct } from '@/adapters/product';
import UnitSelector from '@/components/selector/unit-selector';
import { IBusinessUnit } from '@/interfaces/business-unit';
import { AxiosResponse } from 'axios';
import { queryBusinessUnits } from '@/adapters/business-unit';
// import TableComponent from '@/components/table';
// import Title from '@/components/title';
import { sumBy } from 'lodash';

interface IProps {
  dataModal?: IDataModal<Partial<IProduct>>;
  handleOk: () => void;
  handleCancel: () => void;
}

const ProductAddModal = ({ dataModal, handleOk, handleCancel }: IProps) => {
  const [form] = Form.useForm<IProduct>();
  const [productAllocations, setProductAllocations] = useState<
    IUpdateProductAllocation[]
  >([]);

  const { data: bus } = useQuery<AxiosResponse<IBusinessUnit[]>, Error>({
    queryKey: ['search-bu'],
    queryFn: async () => await queryBusinessUnits(),
  });

  const mutations = useMutation({
    mutationFn: async (data: Partial<IProduct>) =>
      dataModal?.id ? await editProduct(data) : await addProduct(data),
    mutationKey: ['actionProduct'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        const message = dataModal?.id
          ? 'อัพเดทสินค้าสำเร็จ'
          : 'สร้างสินค้าสำเร็จ';
        notification.success({
          message: message,
        });
        setProductAllocations([]);
        handleOk();
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const handleUpdate = async () => {
    form
      .validateFields()
      .then(async (values: Partial<IProduct>) => {
        values.id = Number(dataModal?.id || 0);
        values.productAllocations = productAllocations;
        await mutations.mutateAsync(values);
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  const onReset = () => {
    form.resetFields();
  };

  useEffect(() => {
    if (dataModal?.id) {
      form.setFieldsValue({
        ...dataModal?.item,
      });
    }
  }, [dataModal?.id, dataModal?.item, form]);

  useEffect(() => {
    if (bus?.data) {
      const allos = bus?.data?.map(b => {
        const allo: IUpdateProductAllocation = {
          businessUnitId: b.id,
          isActive: true,
          percentage: 100,
          productId: 0,
          bu: b.name,
        } as IUpdateProductAllocation;
        return allo;
      });
      setProductAllocations(allos);
    }
  }, [bus?.data]);

  const packageUnitId = Form.useWatch('packageUnitId', form);
  const baseUnitId = Form.useWatch('baseUnitId', form);
  const fullPercentage = sumBy(productAllocations, r => r.percentage);
  // const isFullPercentage = fullPercentage == 100;

  // const debouncedSearch = useCallback(
  //   debounce((rec, value: number) => {
  //     setProductAllocations(prev =>
  //       prev.map(item =>
  //         item.businessUnitId == rec.businessUnitId
  //           ? {
  //               ...item,
  //               percentage:
  //                 fullPercentage + Number(value) > 100
  //                   ? 100 - fullPercentage
  //                   : Number(value),
  //             }
  //           : item,
  //       ),
  //     );
  //   }, 300),
  //   [],
  // );

  // const columns: TableProps<IUpdateProductAllocation>['columns'] = [
  //   {
  //     title: 'BU',
  //     dataIndex: 'bu',
  //     key: 'bu',
  //     className: 'font-bold',
  //   },
  //   {
  //     title: 'Percentage',
  //     dataIndex: 'percentage',
  //     key: 'percentage',
  //     className: 'font-bold text-blue-600',
  //     align: 'right',
  //     width: 200,
  //     render: (_, rec) => (
  //       <InputNumber
  //         type="number"
  //         placeholder="Percentage"
  //         className="w-full"
  //         value={rec.percentage}
  //         disabled={isFullPercentage && !rec.percentage}
  //         min={0}
  //         max={100}
  //         onChange={v => debouncedSearch(rec, Number(v))}
  //       />
  //     ),
  //   },
  // ];

  return (
    <Modal
      title={'สินค้าใหม่'}
      open={dataModal?.open}
      onOk={() => handleUpdate()}
      onCancel={() => {
        setProductAllocations([]);
        handleCancel();
      }}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={1000}
      footer={[
        <Button key="back" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Button
          key="submit"
          type="primary"
          form="productGroup"
          loading={mutations.isPending}
          disabled={fullPercentage != 100}
          onClick={handleUpdate}
        >
          บันทึก
        </Button>,
      ]}
      afterClose={onReset}
    >
      <div className="flex w-full">
        <Form
          form={form}
          name="product"
          onFinish={handleUpdate}
          layout="vertical"
          className="w-full"
        >
          <Row gutter={12}>
            <Col span={24} md={8}>
              <Form.Item
                label="รหัสสินค้า"
                name="code"
                rules={[{ required: true, message: 'ระบุรหัสสินค้า' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={16}>
              <Form.Item
                label="ชื่อสินค้า"
                name="name"
                rules={[{ required: true, message: 'ระบุชื่อสินค้า' }]}
              >
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item
                label="หน่วย Package"
                name="packageUnitId"
                rules={[{ required: true, message: 'เลือกหน่วย Package' }]}
              >
                <UnitSelector
                  value={packageUnitId}
                  size="large"
                  handleSelectData={e => form.setFieldValue('packageUnitId', e)}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item
                label="Conversion"
                name="conversion"
                rules={[{ required: true, message: 'ระบุConversion' }]}
              >
                <InputNumber size="large" className="w-full" min={1} />
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item
                label="หน่วยขาย"
                name="baseUnitId"
                rules={[{ required: true, message: 'เลือกหน่วยขาย' }]}
              >
                <UnitSelector
                  value={baseUnitId}
                  size="large"
                  handleSelectData={e => form.setFieldValue('baseUnitId', e)}
                />
              </Form.Item>
            </Col>
            <Col span={24} md={8}>
              <Form.Item
                label="ราคาขาย/หน่วย"
                name="unitPrice"
                rules={[{ required: true, message: 'ระบุราคาขาย' }]}
              >
                <InputNumber size="large" className="w-full" />
              </Form.Item>
            </Col>
            {/* <Col span={24} md={8}>
              <Form.Item<IProduct> label="BU" name="businessUnitId">
                <BusinessUnitSelector
                  handleSelectData={id => {
                    form.setFieldsValue({
                      businessUnitId: id,
                    });
                  }}
                />
              </Form.Item>
            </Col> */}
            <Col span={24}>
              <Form.Item label="หมายเหตุ" name="remark">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
          {/* <Title
            customClassName="text-lg text-blue-700 mb-4"
            customContainerClassName="py-0"
            title={`Cost Allocations`}
          />
          <Row gutter={12}>
            <Col span={24}>
              <TableComponent<IUpdateProductAllocation>
                columns={columns}
                criteria={undefined}
                data={{ data: productAllocations || [], totalRecords: 0 }}
                loading={false}
                onTableChange={undefined}
                showPagination={false}
                scroll={false}
                rowKey="businessUnitId"
              />
            </Col>
          </Row> */}
        </Form>
      </div>
    </Modal>
  );
};

export default ProductAddModal;
